import { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { Box, Button, Container, Divider, Grid, Tab, Tabs, Typography, Link, Breadcrumbs, useTheme } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRightIcon from '../../icons/ChevronRight';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import useSettings from '../../hooks/useSettings';
import useAuth from '../../hooks/useAuth';
import TicketList from '../../components/dashboard/quality/TicketList';
import { ticketApi } from '../../api/ticketApi';
import FormMajorIncident from '../../components/dashboard/quality/FormMajorIncident';
import MajorIncidentList from '../../components/dashboard/quality/MajorIncidentList';
import CreateTicketsMembers from '../../components/dashboard/quality/incidents/create_ticket_member';
import ClientComplaintCreate from '../../components/dashboard/quality/incidents/ClientComplaintCreate';

//Joseph_com Définition des onglets pour les tickets
const tabs = [
  { label: 'Direction', value: 'Direction' },
  { label: 'Informatique', value: 'Informatique' },
  { label: 'Commercial', value: 'Commercial' },
  { label: 'Administratif', value: 'Administratif' },
  { label: 'Mes tickets', value: 'ticketsPerso' }
];

//Joseph_com Définition des onglets pour les réclamations
const tabs_complaint = [
  { label: 'Client', value: 'Réclamation Client' },
  { label: 'Prestataire', value: 'Réclamation Prestataire' },
  { label: 'Formateur', value: 'Réclamation Formateur' },
  { label: 'Collaborateur', value: 'Réclamation Collaborateur' },
  { label: 'Mes réclamations', value: 'ticketsPerso' }
];

const QualityPage = (props) => {
  const { type } = props;
  //Joseph_com État pour stocker la liste des tickets ou réclamations
  const [tickets, setTickets] = useState([]);
  //Joseph_com Récupération des informations de l'utilisateur connecté
  const { user } = useAuth();
  //Joseph_com Récupération des paramètres de l'application
  const { settings } = useSettings();
  const theme = useTheme();
  //Joseph_com État pour gérer l'affichage du loader
  const [isLoading, setIsLoading] = useState(false);
  //Joseph_com État pour suivre l'onglet actuellement sélectionné
  const [currentTab, setCurrentTab] = useState(null);
  //Joseph_com État pour basculer entre la liste des tickets et la liste des incidents majeurs
  const [isListTickets, setIsListTickets] = useState(true);
  //Joseph_com État pour stocker la liste des incidents majeurs
  const [majorIncidents, setMajorIncidents] = useState([]);
  //Joseph_com État pour forcer la mise à jour des listes après une création/modification
  const [update, setUpdate] = useState(false);

  //Joseph_com Gestionnaire de changement d'onglet
  const handleTabsChange = (event, value) => {
    setCurrentTab(value);
  };

  //Joseph_com Fonction pour basculer entre tickets et incidents majeurs
  const listToggle = () => {
    setIsListTickets(!isListTickets);
  };

  //Joseph_com Fonction mémorisée pour récupérer les tickets depuis l'API
  const getTickets = useCallback(async () => {
    //Joseph_com Vérifier que currentTab est défini avant de faire l'appel API
    if (!currentTab) {
      console.log('currentTab is null, skipping API call');
      return;
    }
    
    try {
      setIsLoading(true);
      //Joseph_com Si l'onglet "Mes tickets" est sélectionné, récupérer les tickets créés par l'utilisateur
      if (currentTab === 'ticketsPerso') {
        const data = await ticketApi.getTicketsByCreator(
          window.localStorage.getItem('accessToken'), 
          user.id, 
          type
        );
        setTickets(data);
      } else {
        //Joseph_com Sinon, récupérer les tickets par service
        const data = await ticketApi.getTicketsByServiceName(
          window.localStorage.getItem('accessToken'), 
          currentTab, 
          type
        );
        setTickets(data);
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.error('Erreur lors du chargement des tickets:', err);
    }
  }, [currentTab, user.id, type]);

  //Joseph_com Fonction mémorisée pour récupérer les incidents majeurs d'un service
  const getMajorIncidentsService = useCallback(async () => {
    try {
      setIsLoading(true);
      const majorIncidentsData = await ticketApi.getMajorIncidentsService(
        localStorage.getItem('accessToken'), 
        currentTab
      );
      setMajorIncidents(majorIncidentsData);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.error(e);
    }
  }, [currentTab]);

  //Joseph_com Effect qui se déclenche lors du changement d'onglet, de vue ou après une mise à jour
  useEffect(() => {
    //Joseph_com Ne rien faire si currentTab n'est pas encore initialisé
    if (!currentTab) return;
    
    //Joseph_com Si on affiche la liste des tickets ou l'onglet "Mes tickets"
    if (isListTickets) {
      setTickets([]);
      getTickets();
    } else if (currentTab === 'ticketsPerso') {
      setTickets([]);
      getTickets();
    } else {
      //Joseph_com Sinon, afficher la liste des incidents majeurs
      setMajorIncidents([]);
      getMajorIncidentsService();
    }
  }, [currentTab, isListTickets, update, getTickets, getMajorIncidentsService]);

  //Joseph_com Effect qui se déclenche lors du changement de type (ticket/réclamation)
  useEffect(() => {
    //Joseph_com Définir l'onglet par défaut selon le type et le service de l'utilisateur
    const defaultTab = type === 'ticket' ? user.service : 'Réclamation Client';
    setCurrentTab(defaultTab);
    setTickets([]);
  }, [type, user.service]);

  return (
    <>
      {/* Joseph_com Titre de la page dans l'onglet du navigateur */}
      <Helmet>
        {type === 'ticket' ? (
          <title>Liste des tickets | Syndays</title>
        ) : (
          <title>Liste des réclamations | Syndays</title>
        )}
      </Helmet>
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100%', py: 8 }}>
        <Container maxWidth={settings.compact ? 'xl' : false}>
          {/* ── Hero Header ── */}
          <Box
            sx={{
              mb: 4,
              p: 3.5,
              borderRadius: '24px',
              border: '1px solid',
              borderColor: theme.palette.mode === 'light' ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.05)',
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(241,245,249,0.7) 100%)'
                : 'linear-gradient(135deg, rgba(15,23,42,0.6) 0%, rgba(30,41,59,0.4) 100%)',
              backdropFilter: 'blur(20px)',
              boxShadow: theme.palette.mode === 'light'
                ? '0 4px 24px rgba(0,0,0,0.04)'
                : '0 4px 24px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
                  flexShrink: 0,
                }}
              >
                {type === 'ticket' ? (
                  <ConfirmationNumberIcon sx={{ color: '#fff', fontSize: '1.7rem' }} />
                ) : (
                  <PriorityHighIcon sx={{ color: '#fff', fontSize: '1.7rem' }} />
                )}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  {isListTickets 
                    ? type === "ticket" 
                      ? 'Liste des tickets' 
                      : 'Liste des réclamations' 
                    : type === "ticket" && 'Liste des incidents majeurs'}
                </Typography>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                  sx={{ mt: 1 }}
                >
                  <Link
                    color="textSecondary"
                    component={RouterLink}
                    to="/dashboard"
                    variant="subtitle2"
                    sx={{ textDecoration: 'none', '&:hover': { color: '#3b82f6' } }}
                  >
                    Tableau de bord
                  </Link>
                  <Link
                    color="textSecondary"
                    component={RouterLink}
                    to="/dashboard"
                    variant="subtitle2"
                    sx={{ textDecoration: 'none', '&:hover': { color: '#3b82f6' } }}
                  >
                    Qualité
                  </Link>
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                    sx={{ fontWeight: 600 }}
                  >
                    {type === 'ticket' ? 'Tickets' : 'Réclamations'}
                  </Typography>
                </Breadcrumbs>
              </Box>
            </Box>
          </Box>
          
          {/* Joseph_com Section des onglets */}
          <Box sx={{ mt: 3 }}>
            <Tabs 
              indicatorColor="primary" 
              onChange={handleTabsChange} 
              scrollButtons="auto" 
              textColor="primary" 
              value={currentTab} 
              variant="scrollable"
            >
              {/* Joseph_com Si l'utilisateur est un responsable (access_level 0 et id_service 0) */}
              {(user.access_level === 0 && user.id_service === 0) ? 
                //Joseph_com Afficher tous les onglets selon le type (ticket ou réclamation)
                type === 'ticket' 
                  ? tabs.map((tab) => (<Tab key={tab.value} label={tab.label} value={tab.value} />)) 
                  : tabs_complaint.map((tab) => (<Tab key={tab.value} label={tab.label} value={tab.value} />))
                //Joseph_com Sinon, filtrer les onglets selon le service de l'utilisateur
                : type === 'ticket' 
                  ? tabs.filter((tab) => tab.value === user.service || tab.value === 'ticketsPerso')
                    .map((filteredService) => (
                      <Tab key={filteredService.value} label={filteredService.label} value={filteredService.value} />
                    ))
                  : tabs_complaint.map((filteredService) => (
                      <Tab key={filteredService.value} label={filteredService.label} value={filteredService.value} />
                    ))
              }
            </Tabs>
          </Box>
          <Divider />
          
          {/* Joseph_com Barre d'actions avec boutons */}
          <Box sx={{ my: 2, display: 'flex', justifyContent: currentTab !== 'ticketsPerso' ? 'space-between' : 'flex-end' }}>
            {/* Joseph_com Bouton pour basculer entre tickets et incidents majeurs (uniquement pour les tickets, pas les réclamations) */}
            {currentTab !== 'ticketsPerso' && type === 'ticket' ? (
              <Button color="primary" variant="outlined" onClick={listToggle}>
                {isListTickets ? 'Voir les incidents majeurs' : 'Voir les tickets du service'}
              </Button>
            ) : <Box></Box>}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {/* Joseph_com Formulaire de création d'incident majeur (seulement pour les managers de service) */}
              {(user.service_manager || (user.access_level === 0 && user.id_service === 0)) && 
               currentTab !== 'ticketsPerso' && 
               type === 'ticket' ? (
                <FormMajorIncident 
                  user={user} 
                  serviceTab={currentTab} 
                  update={update} 
                  setUpdate={setUpdate} 
                  isListTickets={isListTickets} 
                  listToggle={listToggle} 
                  setCurrentTab={user.id_service === 0 && setCurrentTab} 
                />
              ) : null}
              
              {/* Joseph_com Réclamation "Client" : modal dédié apprenant -> formation -> session -> texte */}
              {type === 'complaint' && currentTab === 'Réclamation Client' ? (
                <ClientComplaintCreate
                  user={user}
                  update={update}
                  setUpdate={setUpdate}
                  getTickets={getTickets}
                />
              ) : (
                /* Joseph_com Formulaire de création de ticket ou réclamation */
                <CreateTicketsMembers
                  type={type}
                  user={user}
                  update={update}
                  setUpdate={setUpdate}
                  isListTickets={isListTickets}
                  listToggle={listToggle}
                  currentTab={currentTab}
                  setCurrentTab={setCurrentTab}
                  getTickets={getTickets}
                />
              )}
            </Box>
          </Box>
          
          {/* Joseph_com Section d'affichage des listes */}
          <Box sx={{ mt: 1 }}>
            {/* Joseph_com Afficher la liste des tickets/réclamations ou la liste des incidents majeurs */}
            {isListTickets || currentTab === 'ticketsPerso' ? (
              //Joseph_com Liste des tickets ou réclamations
              <TicketList
                type={type}
                tickets={tickets}
                isLoading={isLoading}
                update={update}
                setUpdate={setUpdate}
                getTickets={getTickets}
                currentTab={currentTab}
              />
            ) : (
              //Joseph_com Liste des incidents majeurs
              <MajorIncidentList 
                isLoading={isLoading} 
                majorIncidents={majorIncidents} 
                setMajorIncidents={setMajorIncidents} 
                currentTab={currentTab} 
                updateList={update} 
                setUpdateList={setUpdate} 
              />
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

QualityPage.propTypes = {
  type: PropTypes.string.isRequired,
};

export default QualityPage;